import { useCallback } from 'react';

import { PlanType } from '@showbie-socrative/socrative-utils';
import { Decimal128 } from 'proposal-decimal';

import { BillingPeriod } from '~teacher/components/PricingTable/BillingPeriod';
import { usePricingPlans } from '~teacher/hook/usePlanPrices';

import {
  BillingData,
  PricingPlan,
  PricingPlanData,
  PricingTier,
} from '../../../../../api/sales';

type Pricing = {
  [key in BillingPeriod]: Decimal128;
};

type PreviousAndCurrentPricing = {
  previous?: Pricing;
  current: Pricing;
};

interface UseTieredPricingParams {
  planType: PlanType;
  billingPeriod: BillingPeriod;
  seatCount: number;
}

export function useTieredPricing({
  planType,
  billingPeriod,
  seatCount,
}: UseTieredPricingParams) {
  const select = useCallback(
    (data: PricingPlanData): PreviousAndCurrentPricing => {
      // was that valid plan type?
      const pricingPlan: PricingPlan = data[planType];
      if (!pricingPlan) {
        return {
          current: {
            [BillingPeriod.Yearly]: new Decimal128(0),
            [BillingPeriod.Monthly]: new Decimal128(0),
          },
        };
      }

      const billingData: BillingData = pricingPlan[billingPeriod];
      if (!billingData) {
        return {
          current: {
            [BillingPeriod.Yearly]: new Decimal128(0),
            [BillingPeriod.Monthly]: new Decimal128(0),
          },
        };
      }

      // get the pricing tiers
      const pricingTiers = billingData.tiers;

      const pricingTier = calculatePricingTier({ pricingTiers, seatCount });

      return {
        current: calculatePrice({
          price: new Decimal128(pricingTier.current).divide(
            new Decimal128(100)
          ),
          priceType: billingPeriod,
        }),
        // Only add if there _is_ a previous price tier
        ...(pricingTier.previous && {
          previous: calculatePrice({
            price: new Decimal128(pricingTier.previous).divide(
              new Decimal128(100)
            ),
            priceType: billingPeriod,
          }),
        }),
      };
    },
    [billingPeriod, planType, seatCount]
  );

  return usePricingPlans({
    select: select,
  });
}

type CurrentPreviousNextPrices = {
  current?: number;
  previous?: number;
  next?: number;
};

export function calculatePricingTier({
  pricingTiers,
  seatCount,
}: {
  pricingTiers: Array<PricingTier>;
  seatCount: number;
}): CurrentPreviousNextPrices {
  const sortedTiers = pricingTiers
    // Convert the Null into Infinity so that we can sort it later.
    .map(({ priceCents, upTo }) => ({
      priceCents,
      upTo: upTo === null ? Infinity : upTo,
    }))
    .sort((a, b) => a.upTo - b.upTo);

  // Find the price tier that the seat count falls into
  const priceTierIndex = sortedTiers.findIndex(
    ({ upTo }: PricingTier) => seatCount <= upTo
  );

  // Return the current, previous, and next price tiers
  // previous and next are backwards since the array is sorted in descending order
  return {
    previous: sortedTiers[priceTierIndex - 1]?.priceCents,
    current: sortedTiers[priceTierIndex].priceCents,
    next: sortedTiers[priceTierIndex + 1]?.priceCents,
  };
}

interface CalculatePriceParams {
  price: Decimal128;
  priceType: BillingPeriod;
}

function calculatePrice({ price, priceType }: CalculatePriceParams) {
  if (price === undefined) {
    return undefined;
  }
  return {
    [BillingPeriod.Yearly]: calculateAnnualPrice({
      price,
      priceType,
    }),
    [BillingPeriod.Monthly]: calculateMonthlyPrice({
      price,
      priceType,
    }),
  };
}

interface CalculateAnnualPriceParams {
  price: Decimal128;
  priceType: BillingPeriod;
}
export function calculateAnnualPrice({
  price,
  priceType,
}: CalculateAnnualPriceParams): Decimal128 {
  switch (priceType) {
    case BillingPeriod.Monthly:
      return price.multiply(new Decimal128(12));
    case BillingPeriod.Yearly:
      return price;
    default:
      return price;
  }
}

interface CalculateMonthlyPriceParams {
  price: Decimal128;
  priceType: BillingPeriod;
}

export function calculateMonthlyPrice({
  price,
  priceType,
}: CalculateMonthlyPriceParams): Decimal128 {
  switch (priceType) {
    case BillingPeriod.Monthly:
      return price;
    case BillingPeriod.Yearly:
      return price.divide(new Decimal128(12));
    default:
      return price;
  }
}
